import React, { useState } from 'react';
import { Search, Filter, Building2, Camera, ClipboardList, Clock, AlertCircle, CheckCircle, PlayCircle, ArrowRight, ChevronDown, Check, Circle, ChevronRight } from 'lucide-react';
// --- Types ---
type Status = 'In Corso' | 'Completato' | 'Da Verificare';
interface ChecklistItem {
  label: string;
  completed: boolean;
}
interface Intervention {
  id: number;
  title: string;
  property: string;
  address: string;
  status: Status;
  fornitore: {
    name: string;
    initials: string;
    avatarColor: string;
  };
  checklist: {
    completed: number;
    total: number;
    items?: ChecklistItem[]; // Optional detailed items
  };
  photos: {
    count: number;
    isUpdating?: boolean;
  };
  duration: {
    text: string;
    subtext: string;
  };
  completionPercent?: number; // For 'In Corso'
  completedDate?: string; // For 'Completato'
  payment: {
    status: 'Pagato' | 'In attesa' | 'Da approvare';
    amount: string;
  };
}
// --- Mock Data ---
const mockInterventions: Intervention[] = [{
  id: 1,
  title: 'Pulizia post check-out',
  property: 'Appartamento Prati',
  address: 'Via Cola di Rienzo 28',
  status: 'In Corso',
  fornitore: {
    name: 'Sofia Bianchi',
    initials: 'SB',
    avatarColor: 'bg-teal-100 text-teal-700'
  },
  checklist: {
    completed: 4,
    total: 6,
    items: [{
      label: 'Cambio biancheria',
      completed: true
    }, {
      label: 'Pulizia bagni',
      completed: true
    }, {
      label: 'Aspirapolvere',
      completed: true
    }, {
      label: 'Cucina e elettrodomestici',
      completed: false
    }, {
      label: 'Rifornimento kit cortesia',
      completed: false
    }, {
      label: 'Controllo finale',
      completed: false
    }]
  },
  photos: {
    count: 3,
    isUpdating: true
  },
  duration: {
    text: '1h 23m',
    subtext: 'iniziato alle 10:00'
  },
  completionPercent: 67,
  payment: {
    status: 'In attesa',
    amount: '€85'
  }
}, {
  id: 2,
  title: 'Sanificazione completa',
  property: 'Suite Testaccio',
  address: 'Via Marmorata 32',
  status: 'In Corso',
  fornitore: {
    name: 'Marco R.',
    initials: 'MR',
    avatarColor: 'bg-blue-100 text-blue-700'
  },
  checklist: {
    completed: 2,
    total: 8
  },
  photos: {
    count: 1,
    isUpdating: true
  },
  duration: {
    text: '0h 45m',
    subtext: 'iniziato 11:15'
  },
  completionPercent: 25,
  payment: {
    status: 'In attesa',
    amount: '€120'
  }
}, {
  id: 3,
  title: 'Pulizia ordinaria',
  property: 'Loft Centro Storico',
  address: 'Via del Corso 150',
  status: 'Da Verificare',
  fornitore: {
    name: 'Lucia M.',
    initials: 'LM',
    avatarColor: 'bg-pink-100 text-pink-700'
  },
  checklist: {
    completed: 5,
    total: 5
  },
  photos: {
    count: 6
  },
  duration: {
    text: '2h 15m',
    subtext: '09:00-11:15'
  },
  payment: {
    status: 'Da approvare',
    amount: '€75'
  }
}, {
  id: 4,
  title: 'Pulizia profonda trimestrale',
  property: 'Appartamento Prati',
  address: 'Via Cola di Rienzo 28',
  status: 'Completato',
  fornitore: {
    name: 'Sofia Bianchi',
    initials: 'SB',
    avatarColor: 'bg-teal-100 text-teal-700'
  },
  checklist: {
    completed: 6,
    total: 6
  },
  photos: {
    count: 8
  },
  duration: {
    text: '3h 00m',
    subtext: '09:00-12:00'
  },
  completedDate: '07 Feb',
  payment: {
    status: 'Pagato',
    amount: '€95'
  }
}, {
  id: 5,
  title: 'Cambio biancheria express',
  property: 'Bilocale Trastevere',
  address: 'Via della Lungaretta 12',
  status: 'Completato',
  fornitore: {
    name: 'Francesca N.',
    initials: 'FN',
    avatarColor: 'bg-orange-100 text-orange-700'
  },
  checklist: {
    completed: 4,
    total: 4
  },
  photos: {
    count: 4
  },
  duration: {
    text: '1h 30m',
    subtext: '14:00-15:30'
  },
  completedDate: '06 Feb',
  payment: {
    status: 'Pagato',
    amount: '€55'
  }
}, {
  id: 6,
  title: 'Check-out pulizia standard',
  property: 'Loft Centro Storico',
  address: 'Via del Corso 150',
  status: 'Completato',
  fornitore: {
    name: 'Marco R.',
    initials: 'MR',
    avatarColor: 'bg-blue-100 text-blue-700'
  },
  checklist: {
    completed: 5,
    total: 5
  },
  photos: {
    count: 5
  },
  duration: {
    text: '2h 00m',
    subtext: '10:00-12:00'
  },
  completedDate: '05 Feb',
  payment: {
    status: 'Pagato',
    amount: '€70'
  }
}];
export function HostMonitoraggioPage() {
  const [activeTab, setActiveTab] = useState<'Tutti' | 'In Corso' | 'Completati' | 'Da Verificare'>('Tutti');
  const [searchQuery, setSearchQuery] = useState('');
  // Filter Logic
  const filteredInterventions = mockInterventions.filter(item => {
    // Tab Filter
    if (activeTab === 'In Corso' && item.status !== 'In Corso') return false;
    if (activeTab === 'Completati' && item.status !== 'Completato') return false;
    if (activeTab === 'Da Verificare' && item.status !== 'Da Verificare') return false;
    // Search Filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return item.property.toLowerCase().includes(query) || item.address.toLowerCase().includes(query) || item.fornitore.name.toLowerCase().includes(query);
    }
    return true;
  });
  // --- Helper Render Functions ---
  const getStatusBadge = (status: Status) => {
    switch (status) {
      case 'In Corso':
        return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs lg:text-[10px] font-bold uppercase tracking-wider bg-blue-50 text-blue-700 border border-blue-100" data-id="element-504">
            <span className="relative flex h-2 w-2" data-id="element-505">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" data-id="element-506"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" data-id="element-507"></span>
            </span>
            <PlayCircle size={10} data-id="element-508" />
            In Corso
          </span>;
      case 'Completato':
        return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs lg:text-[10px] font-bold uppercase tracking-wider bg-green-50 text-green-700 border border-green-100" data-id="element-509">
            <CheckCircle size={10} data-id="element-510" />
            Completato
          </span>;
      case 'Da Verificare':
        return <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs lg:text-[10px] font-bold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-100" data-id="element-511">
            <AlertCircle size={10} data-id="element-512" />
            Da Verificare
          </span>;
    }
  };
  const getCardBorderClass = (status: Status) => {
    switch (status) {
      case 'In Corso':
        return 'bg-blue-50/30';
      case 'Da Verificare':
        return '';
      case 'Completato':
        return 'opacity-90';
    }
  };
  const getPaymentBadge = (payment: Intervention['payment']) => {
    switch (payment.status) {
      case 'Pagato':
        return <span className="inline-flex items-center gap-1 px-2.5 py-1 lg:px-2 lg:py-0.5 rounded-full text-xs lg:text-[10px] font-medium bg-green-50 text-green-700 border border-green-100" data-id="element-513">
            Pagato {payment.amount}
          </span>;
      case 'In attesa':
        return <span className="inline-flex items-center gap-1 px-2.5 py-1 lg:px-2 lg:py-0.5 rounded-full text-xs lg:text-[10px] font-medium bg-gray-100 text-gray-600 border border-gray-200" data-id="element-514">
            In attesa {payment.amount}
          </span>;
      case 'Da approvare':
        return <span className="inline-flex items-center gap-1 px-2.5 py-1 lg:px-2 lg:py-0.5 rounded-full text-xs lg:text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-100" data-id="element-515">
            Da approvare {payment.amount}
          </span>;
    }
  };
  return <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-6" data-id="element-516">
      <style data-id="element-517">{`
        @keyframes stripe-move {
          0% { background-position: 100% 0; }
          100% { background-position: 0 0; }
        }
        .animate-stripe {
          background-image: repeating-linear-gradient(
            45deg,
            transparent,
            transparent 10px,
            rgba(255, 255, 255, 0.2) 10px,
            rgba(255, 255, 255, 0.2) 20px
          );
          background-size: 200% 100%;
          animation: stripe-move 1s linear infinite;
        }
      `}</style>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4" data-id="element-518">
        <div data-id="element-519">
          <h1 className="text-2xl lg:text-2xl font-bold font-montserrat text-navy mb-1" data-id="element-520">
            Monitoraggio Interventi
          </h1>
          <p className="text-sm text-gray-500" data-id="element-521">
            Controlla l'avanzamento e lo storico degli interventi sulle tue
            proprietà
          </p>
        </div>
      </div>

      {/* Tabs & Filters */}
      <div className="flex flex-col gap-4" data-id="element-522">
        {/* Horizontal Tabs */}
        <div className="border-b border-gray-200 flex gap-4 lg:gap-6 overflow-x-auto" data-id="element-523">
          {[{
          id: 'Tutti',
          label: 'Tutti',
          count: 6
        }, {
          id: 'In Corso',
          label: 'In Corso',
          count: 2,
          pulse: true
        }, {
          id: 'Completati',
          label: 'Completati',
          count: 3
        }, {
          id: 'Da Verificare',
          label: 'Da Verificare',
          count: 1
        }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id as any)} className={`pb-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-2 ${activeTab === tab.id ? 'border-teal-deep text-teal-deep' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-524">
              {tab.label}
              <div className="flex items-center gap-1" data-id="element-525">
                {tab.pulse && <span className="relative flex h-2 w-2" data-id="element-526">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" data-id="element-527"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-blue-500" data-id="element-528"></span>
                  </span>}
                <span className={`text-xs lg:text-[10px] px-1.5 py-0.5 rounded-full ${activeTab === tab.id ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-529">
                  {tab.count}
                </span>
              </div>
            </button>)}
        </div>

        {/* Filter Row */}
        <div className="flex flex-col md:flex-row gap-3 items-center justify-between" data-id="element-530">
          <div className="relative w-full md:w-80" data-id="element-531">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" data-id="element-532" />
            <input type="text" placeholder="Cerca per proprietà, fornitore o indirizzo..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-3 lg:py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none min-h-[48px] lg:min-h-[40px]" data-id="element-533" />
          </div>
          <div className="flex gap-3 w-full md:w-auto" data-id="element-534">
            <button className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-3 lg:py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 hover:text-navy transition-colors bg-white min-h-[48px] lg:min-h-[40px]" data-id="element-535">
              <Filter size={16} data-id="element-536" />
              <span data-id="element-537">Filtra</span>
              <ChevronDown size={14} className="text-gray-400" data-id="element-538" />
            </button>
          </div>
        </div>
      </div>

      {/* Interventions Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-539">
        {filteredInterventions.length > 0 ? filteredInterventions.map(item => <div key={item.id} className="group relative bg-white border border-gray-200 rounded-xl p-5 hover:shadow-sm transition-all cursor-pointer" data-id="element-540">
              {/* Header: Status + Duration */}
              <div className="flex justify-between items-center mb-3" data-id="element-541">
                {getStatusBadge(item.status)}
                <span className="text-xs font-medium text-gray-400" data-id="element-542">
                  {item.duration.text}
                </span>
              </div>

              {/* Title & Property */}
              <h3 className="text-base font-semibold text-navy mb-1 pr-8" data-id="element-543">
                {item.title}
              </h3>
              <p className="text-sm text-gray-500 mb-5" data-id="element-544">{item.property}</p>

              {/* Footer: Fornitore + Progress/Status */}
              <div className="flex items-center justify-between" data-id="element-545">
                <div className="flex items-center gap-2" data-id="element-546">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold ${item.fornitore.avatarColor}`} data-id="element-547">
                    {item.fornitore.initials}
                  </div>
                  <span className="text-xs text-gray-600 font-medium" data-id="element-548">
                    {item.fornitore.name}
                  </span>
                </div>

                {item.status === 'In Corso' && item.completionPercent && <div className="flex items-center gap-2" data-id="element-549">
                    <div className="w-16 bg-gray-100 rounded-full h-1.5" data-id="element-550">
                      <div className={`h-1.5 rounded-full bg-blue-500`} style={{
                width: `${item.completionPercent}%`
              }} data-id="element-551" />
                    </div>
                    <span className="text-xs font-bold text-blue-600" data-id="element-552">
                      {item.completionPercent}%
                    </span>
                  </div>}

                {item.status === 'Completato' && <span className="text-xs text-gray-400 flex items-center gap-1" data-id="element-553">
                    <Check size={12} className="text-green-500" data-id="element-554" />
                    {item.completedDate}
                  </span>}
              </div>

              {/* Navigation Hint */}
              <div className="absolute top-1/2 right-4 -translate-y-1/2 text-gray-300 group-hover:text-teal-deep transition-colors" data-id="element-555">
                <ChevronRight size={20} data-id="element-556" />
              </div>
            </div>) : <div className="col-span-full text-center py-12 bg-white rounded-xl border border-gray-200 border-dashed" data-id="element-557">
            <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-3" data-id="element-558">
              <Search className="text-gray-400" data-id="element-559" />
            </div>
            <h3 className="text-base lg:text-sm font-bold text-navy mb-1" data-id="element-560">
              Nessun intervento trovato
            </h3>
            <p className="text-sm lg:text-xs text-gray-500" data-id="element-561">
              Prova a modificare i filtri di ricerca
            </p>
          </div>}
      </div>
    </div>;
}